import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkGemoji from 'remark-gemoji';
import remarkMath from 'remark-math';
import remarkDeflist from 'remark-deflist';
import remarkAlert from 'remark-github-blockquote-alert';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import rehypeSlug from 'rehype-slug';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { CodeContent } from './code-content';
import { MermaidDiagram } from './mermaid-diagram';
import { Checkbox } from './checkbox';
import { CopyButton } from './CopyButton';
import { MarkdownLink } from './markdown-link';
import 'katex/dist/katex.min.css';
// Note: we intentionally do NOT import remark-github-blockquote-alert/alert.css —
// our own .markdown-alert rules in src/index.css drive the styling off theme tokens.

// Sanitize schema extending the GitHub-style default with what KaTeX,
// the alert plugin, and the GFM extension test exercise.
//
// Trust model: markdown content comes from our own agents and console
// users. We sanitize as defense-in-depth (script-stripping is enforced
// by defaultSchema) but intentionally allow `className` and a narrow
// set of `style` attributes that KaTeX needs for positioning.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    '*': [
      ...((defaultSchema.attributes?.['*'] as Array<unknown> | undefined) ?? []),
      'className',
    ],
    // KaTeX's HTML output uses inline styles for offset positioning of glyphs.
    span: [
      ...((defaultSchema.attributes?.span as Array<unknown> | undefined) ?? []),
      'className',
      'style',
    ],
    div: [
      ...((defaultSchema.attributes?.div as Array<unknown> | undefined) ?? []),
      'className',
    ],
    // GFM task lists emit `<input type="checkbox" checked? disabled />`.
    // The default schema only allows `disabled` and `type`, so `checked`
    // is normally stripped — add it so our component override sees the state.
    input: [
      ...((defaultSchema.attributes?.input as Array<unknown> | undefined) ?? []),
      'checked',
    ],
    // remark-github-blockquote-alert wraps alerts in <svg> icons.
    svg: ['xmlns', 'viewBox', 'width', 'height', 'fill', 'aria-hidden', 'className'],
    path: ['d', 'fillRule', 'clipRule'],
  },
  tagNames: [
    ...((defaultSchema.tagNames as string[] | undefined) ?? []),
    // KaTeX MathML output
    'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'msubsup',
    'mfrac', 'msqrt', 'mroot', 'mtext', 'mspace', 'mstyle', 'mtable', 'mtr',
    'mtd', 'mover', 'munder', 'munderover', 'mphantom', 'annotation',
    // Alert plugin icons
    'svg', 'path',
  ],
};

interface MarkdownContentProps {
  children: string;
  size?: 'xs' | 'sm';
  className?: string;
  /**
   * When provided, any inline `<code>` element whose exact text matches a value
   * in this list is rendered with a small copy button. Omitted by default — no
   * behaviour change for existing callers.
   */
  copyableValues?: string[];
}

function CodeBlock({ language, children }: { language: string | undefined; children: string }) {
  // A missing or literal "unknown" fence language is not a real language —
  // render the block with no title (empty label) rather than labelling it
  // "unknown"/"text". The copy + line-number chrome still shows.
  const known = language && language.toLowerCase() !== 'unknown' ? language : undefined;
  return (
    <CodeContent
      language={known ?? 'text'}
      label={known ?? ''}
      className="my-2"
    >
      {children}
    </CodeContent>
  );
}

// Some upstream producers (notably agents that JSON.stringify their output
// before handing it to a tool) persist markdown with literal `\n` two-char
// sequences instead of real newlines, which leaves react-markdown reading the
// whole blob as one logical line and silently breaking every block-level
// construct. The `s.includes('\n')` short-circuit means well-formed input is
// untouched, so this can't munge code blocks containing the characters `\n`.
function normalizeEscapedNewlines(s: string): string {
  if (s.includes('\n')) return s;
  if (!/\\[ntr]/.test(s)) return s;
  return s.replace(/\\n/g, '\n').replace(/\\r/g, '\r').replace(/\\t/g, '\t');
}

interface InlineMarkdownProps {
  children: string;
  className?: string;
}

/**
 * Lightweight markdown renderer for glanceable surfaces (e.g. the
 * chain-of-thought narration row). Renders only inline emphasis — **bold**,
 * *italic*, ~~strikethrough~~, inline `code`, and links — and degrades every
 * heavier construct (tables, headings, code blocks, lists, blockquotes, math,
 * alerts) to plain inline text via `unwrapDisallowed`.
 *
 * This deliberately avoids the full remark/rehype stack so that a verbose
 * final answer leaking into the CoT mid-run can't render a real (broken)
 * table or code block inside a clamped row.
 */
export function InlineMarkdown({ children, className = '' }: InlineMarkdownProps) {
  const source = normalizeEscapedNewlines(children);
  return (
    <div className={className}>
      <Markdown
        // remark-gfm only for ~~strikethrough~~; any tables it parses get
        // unwrapped to text by allowedElements + unwrapDisallowed below.
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
        allowedElements={['p', 'em', 'strong', 'del', 'code', 'a', 'br']}
        unwrapDisallowed
        components={{
          a: MarkdownLink,
          // Inline code only — a fenced block's <pre> is unwrapped, so its
          // <code> child collapses to this inline rendering.
          code({ children: codeChildren, ...props }) {
            return (
              <code className="bg-muted px-1 py-0.5 rounded text-[0.9em] font-mono" {...props}>
                {codeChildren}
              </code>
            );
          },
        }}
      >
        {source}
      </Markdown>
    </div>
  );
}

export function MarkdownContent({
  children,
  size = 'xs',
  className = '',
  copyableValues,
}: MarkdownContentProps) {
  const source = normalizeEscapedNewlines(children);
  const copyableSet = copyableValues && copyableValues.length > 0
    ? new Set(copyableValues)
    : null;
  return (
    <div className={`prose prose-${size} dark:prose-invert max-w-none
      prose-headings:font-semibold
      prose-h1:text-lg prose-h1:mt-8 prose-h1:mb-4
      prose-h2:text-base prose-h2:mt-8 prose-h2:mb-3
      prose-h3:text-sm prose-h3:mt-6 prose-h3:mb-2.5
      prose-h4:text-sm prose-h4:font-medium prose-h4:mt-6 prose-h4:mb-2
      prose-h5:text-xs prose-h5:font-medium prose-h5:mt-4 prose-h5:mb-2
      prose-h6:text-xs prose-h6:font-medium prose-h6:mt-4 prose-h6:mb-2
      [&>:first-child]:mt-0
      prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1
      prose-a:text-blue-600 dark:prose-a:text-blue-400
      prose-a:no-underline [&_a:hover]:underline [&_a:hover]:underline-offset-2
      prose-strong:font-semibold
      prose-code:before:content-none prose-code:after:content-none
      prose-pre:m-0 prose-pre:p-0 prose-pre:bg-transparent
      prose-table:my-2 prose-table:text-xs
      prose-th:border prose-th:border-border prose-th:px-2 prose-th:py-1 prose-th:bg-muted prose-th:text-left prose-th:font-medium
      prose-td:border prose-td:border-border prose-td:px-2 prose-td:py-1
      [&_li.task-list-item]:list-none [&_li.task-list-item]:pl-0
      ${className}`}>
      <Markdown
        remarkPlugins={[
          remarkGfm,
          remarkGemoji,
          [remarkMath, { singleDollarTextMath: false }],
          remarkDeflist,
          remarkAlert,
        ]}
        rehypePlugins={[
          // rehype-raw parses raw HTML in source into the tree.
          // rehype-katex transforms math placeholders into KaTeX output.
          // rehype-slug adds id attrs to headings.
          // rehype-sanitize runs LAST to strip anything not in the allowlist
          // (the schema above explicitly allows KaTeX's MathML/spans and the
          // alert plugin's svg/path nodes).
          rehypeRaw,
          rehypeKatex,
          rehypeSlug,
          [rehypeSanitize, sanitizeSchema],
        ]}
        components={{
          // External links open in a new tab; same-origin/relative stay in-place.
          a: MarkdownLink,
          code({ className: codeClassName, children: codeChildren, ...props }) {
            const match = /language-(\w+)/.exec(codeClassName || '');
            const content = String(codeChildren).replace(/\n$/, '');

            // Fenced code block: rendered inside <pre> by react-markdown
            // Detect by className (language-*) or by the presence of newlines
            if (match || (codeClassName && codeClassName.includes('language-'))) {
              // ```mermaid fences render as inline SVG diagrams (falling back
              // to a labelled code block on parse errors — see MermaidDiagram).
              if (match?.[1] === 'mermaid') {
                return <MermaidDiagram code={content} className="my-2" />;
              }
              return <CodeBlock language={match?.[1]}>{content}</CodeBlock>;
            }

            // Multi-line code without explicit language (bare ```)
            if (content.includes('\n')) {
              return <CodeBlock language={undefined}>{content}</CodeBlock>;
            }

            // Inline code
            if (copyableSet && copyableSet.has(content)) {
              return (
                <span className="inline-flex items-center gap-1 align-baseline">
                  <code className="bg-muted px-1 py-0.5 rounded text-[0.9em] font-mono" {...props}>
                    {codeChildren}
                  </code>
                  <CopyButton
                    value={content}
                    size="sm"
                    className="!p-0.5"
                    title="Copy value"
                  />
                </span>
              );
            }
            return (
              <code className="bg-muted px-1 py-0.5 rounded text-[0.9em] font-mono" {...props}>
                {codeChildren}
              </code>
            );
          },
          // Strip the <pre> wrapper for fenced blocks since CodeBlock provides its own
          pre({ children: preChildren }) {
            return <>{preChildren}</>;
          },
          // GFM task list checkboxes — replace the OS-native input with the
          // styled Radix checkbox the rest of the app uses.
          input(props) {
            if (props.type === 'checkbox') {
              return (
                <Checkbox
                  checked={!!props.checked}
                  disabled
                  className="mr-1.5 inline-flex translate-y-[2px]"
                />
              );
            }
            return <input {...props} />;
          },
        }}
      >
        {source}
      </Markdown>
    </div>
  );
}
